import type { InputEvent, InputProvider } from './InputProvider';

export interface ButtonDef {
  x: number;
  y: number;
  w: number;
  h: number;
  keyCode: string;
}

export class MouseInput implements InputProvider {
  readonly name = 'mouse';
  onInput: ((event: InputEvent) => void) | null = null;

  private _element: HTMLElement | null = null;
  private _suspended = false;
  private _buttonDefs: ButtonDef[];
  private _scale: number;
  private _activeKey: string | null = null;
  private _pressedButtons: Set<string> = new Set();

  private _handleMouseDown = (e: MouseEvent): void => {
    if (this._suspended) return;
    const hit = this._hitTest(e.offsetX, e.offsetY);
    if (hit) {
      this._press(hit.keyCode);
    }
  };

  private _handleMouseUp = (_e: MouseEvent): void => {
    if (this._suspended) return;
    if (this._activeKey) {
      this._release(this._activeKey);
    }
  };

  private _handleMouseMove = (e: MouseEvent): void => {
    if (this._suspended) return;
    if (!this._activeKey) return;
    const hit = this._hitTest(e.offsetX, e.offsetY);
    if (hit && hit.keyCode !== this._activeKey) {
      this._release(this._activeKey);
      this._press(hit.keyCode);
    } else if (!hit) {
      this._release(this._activeKey);
    }
  };

  private _handleMouseLeave = (): void => {
    if (this._activeKey) {
      this._release(this._activeKey);
    }
  };

  constructor(buttonDefs: ButtonDef[], scale: number) {
    this._buttonDefs = buttonDefs;
    this._scale = scale;
  }

  attach(element: HTMLElement): void {
    if (this._element) {
      this.detach();
    }
    this._element = element;
    element.addEventListener('mousedown', this._handleMouseDown);
    element.addEventListener('mouseup', this._handleMouseUp);
    element.addEventListener('mousemove', this._handleMouseMove);
    element.addEventListener('mouseleave', this._handleMouseLeave);
  }

  detach(): void {
    if (this._element) {
      this._element.removeEventListener('mousedown', this._handleMouseDown);
      this._element.removeEventListener('mouseup', this._handleMouseUp);
      this._element.removeEventListener('mousemove', this._handleMouseMove);
      this._element.removeEventListener('mouseleave', this._handleMouseLeave);
      this._element = null;
    }
    this._activeKey = null;
    this._pressedButtons.clear();
  }

  suspend(): void {
    this._suspended = true;
  }

  resume(): void {
    this._suspended = false;
  }

  hitTest(x: number, y: number): ButtonDef | null {
    return this._hitTest(x, y);
  }

  private _hitTest(x: number, y: number): ButtonDef | null {
    for (const def of this._buttonDefs) {
      const left = def.x * this._scale;
      const top = def.y * this._scale;
      const right = (def.x + def.w) * this._scale;
      const bottom = (def.y + def.h) * this._scale;
      if (x >= left && x < right && y >= top && y < bottom) {
        return def;
      }
    }
    return null;
  }

  private _press(keyCode: string): void {
    if (this._pressedButtons.has(keyCode)) return;
    this._pressedButtons.add(keyCode);
    this._activeKey = keyCode;
    this._fire(keyCode, true);
  }

  private _release(keyCode: string): void {
    if (!this._pressedButtons.has(keyCode)) return;
    this._pressedButtons.delete(keyCode);
    if (this._activeKey === keyCode) {
      this._activeKey = null;
    }
    this._fire(keyCode, false);
  }

  private _fire(keyCode: string, pressed: boolean): void {
    if (this.onInput) {
      this.onInput({ keyCode, scanCode: 0, pressed, timestamp: Date.now() });
    }
  }
}
